import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Flag, FlagOff } from "lucide-react";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import type {
  AddLineInput,
  CaseDetail,
  CaseHeaderPatch,
  DuplicateCandidate,
  PartLineView,
  UpdateLineInput,
} from "../../../../shared/contracts.js";
import { LIMITS } from "../../../../shared/contracts.js";
import { PART_EXCEPTIONS } from "../../../../shared/domain.js";
import type { PartException } from "../../../../shared/domain.js";
import { normalizeName, normalizeVin } from "../../../../shared/formatting.js";
import { useAssignees } from "../../../api/accountQueries.js";
import { caseQueryKeys } from "../../../api/caseQueries.js";
import {
  addLine,
  duplicateCandidatesOf,
  fieldErrorsOf,
  invalidateCaseData,
  isStaleWriteError,
  orderMutationErrorMessage,
  setLineException,
  STALE_WRITE_MESSAGE,
  updateCaseHeader,
  updateLine,
  useSetLineException,
  type CaseMutationResultDto,
  type LineExceptionEndpoint,
  type UpdateCaseHeaderInput,
} from "../../../api/caseMutations.js";
import { Button } from "../../../ui/Button.js";
import { ConfirmDialog, Dialog, DialogActions } from "../../../ui/Dialog.js";
import { FormField } from "../../../ui/FormField.js";
import { DuplicateWarningDialog } from "../order-form/DuplicateWarningDialog.js";
import {
  clearChangedLineMarks,
  focusFirstInvalid,
  isOrderFormFieldKey,
  lineFieldKey,
  MasterEtaField,
  newClientId,
  newLineValues,
  NO_ERRORS,
  OptionalDetailsFields,
  OrderFormSection,
  parseWholeNumber,
  PeopleFields,
  reasonError,
  toEtaInput,
  validateHeader,
  validateLine,
  VehicleAndVisitFields,
  withMasterEta,
  withoutBlankTrailingLines,
  type OrderFormErrors,
  type OrderFormFieldKey,
  type OrderFormHeaderValues,
  type OrderFormLineErrors,
  type OrderFormLineValues,
} from "../order-form/OrderForm.js";
import { PartLinesGrid } from "../order-form/PartLinesGrid.js";
import { exceptionLabel } from "./VerifyArrivalDialog.js";

/**
 * The Parts edit sheet (spec §2 "edit vehicle, RO, assignment, and order
 * information; add, edit, cancel, or flag part lines; update ETAs").
 *
 * The sheet opens on a snapshot of the case — its version, header and
 * lines — and works from that snapshot until it closes, so a background
 * refetch never rewrites what Parts is typing or lets a save ride on a
 * version they never saw. Saving turns the difference between the snapshot
 * and the form into the server's own commands, in order: one header
 * `PATCH`, one `PATCH` per changed line, one `POST` per new line. Each
 * carries the version the previous answer returned. A step that fails stops
 * the run; what already saved stays saved and is folded into the snapshot,
 * so pressing Save again sends only what is still unsaved.
 *
 * - 409 `STALE_WRITE`: the sheet says so and offers Reload latest — it never
 *   saves over a newer version.
 * - 409 `DUPLICATE_CASE` (an RO already on another open order, or a VIN
 *   added or replaced whose vehicle already has one of these parts on another
 *   open order): the duplicate warning with links; Save anyway resubmits with
 *   the audited override.
 * - 422: the server's field errors land beside the fields they name.
 *
 * Exceptions are their own audited command (`POST …/exception`) with a
 * reason, flagged or cleared from a line at once through
 * {@link LineExceptionDialog}; they bump the version the sheet then uses.
 * Existing lines are records: they can be flagged as a cancelled line, never
 * removed. On an Arrived case, raising a quantity or adding a line needs a
 * correction reason (spec §7 "Corrections after arrival", §17).
 */

// ---------------------------------------------------------------------------
// Editing values with the server facts the sheet needs beside them
// ---------------------------------------------------------------------------

export type EditLineValues = OrderFormLineValues & {
  /** The server's line id; `null` for a line added in this sheet. */
  lineId: string | null;
  quantityVerified: number;
  exception: PartException | null;
  exceptionReason: string | null;
};

export type EditOrderValues = OrderFormHeaderValues & {
  partLines: EditLineValues[];
};

export function lineValuesFromView(line: PartLineView): EditLineValues {
  return {
    clientId: line.id,
    lineId: line.id,
    partNumber: line.partNumber,
    description: line.description,
    quantityOrdered: String(line.quantityOrdered),
    etaDate: line.eta.kind === "date" ? line.eta.date : "",
    noEta: line.eta.kind === "no_eta",
    quantityVerified: line.quantityVerified,
    exception: line.exception,
    exceptionReason: line.exceptionReason,
  };
}

export function valuesFromDetail(detail: CaseDetail): EditOrderValues {
  const record = detail.case;
  return {
    vin: record.vin ?? "",
    roNumber: record.roNumber ?? "",
    customerName: record.customerName,
    vehicleYear: String(record.vehicleYear),
    vehicleMake: record.vehicleMake,
    vehicleModel: record.vehicleModel,
    advisorId: record.advisorId,
    technicianId: record.technicianId ?? "",
    appointmentDate: record.appointmentDate ?? "",
    vendorOrderReference: record.vendorOrderReference ?? "",
    orderNote: record.orderNote ?? "",
    masterEta: record.masterEta ?? "",
    carInShop: record.carInShop,
    partLines: detail.partLines.map(lineValuesFromView),
  };
}

/** A new line on the sheet starts from the order's "ETA for all parts" when it has one. */
function newEditLine(masterEta = ""): EditLineValues {
  return {
    ...newLineValues(masterEta),
    clientId: newClientId(),
    lineId: null,
    quantityVerified: 0,
    exception: null,
    exceptionReason: null,
  };
}

// ---------------------------------------------------------------------------
// The save plan: from the snapshot and the form to the server's commands
// ---------------------------------------------------------------------------

export type SaveStep =
  | Readonly<{ kind: "header"; patch: CaseHeaderPatch }>
  | Readonly<{ kind: "line"; clientId: string; lineId: string; patch: UpdateLineInput["patch"] }>
  | Readonly<{ kind: "add"; clientId: string; input: Omit<AddLineInput, "meta"> }>;

function trimmedOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * The header fields whose value differs from the snapshot, as the server
 * takes them. An emptied VIN or an unassigned technician travels as `null`
 * (the clearing value), the same way the optional details do.
 */
export function headerPatch(baseline: OrderFormHeaderValues, values: OrderFormHeaderValues): CaseHeaderPatch {
  const patch: CaseHeaderPatch = {};
  if (normalizeVin(values.vin) !== normalizeVin(baseline.vin)) patch.vin = trimmedOrNull(values.vin);

  const ro = trimmedOrNull(normalizeName(values.roNumber));
  if (ro !== trimmedOrNull(normalizeName(baseline.roNumber))) patch.roNumber = ro;

  if (normalizeName(values.customerName) !== normalizeName(baseline.customerName)) {
    patch.customerName = values.customerName.trim();
  }
  const year = parseWholeNumber(values.vehicleYear);
  if (year !== null && year !== parseWholeNumber(baseline.vehicleYear)) patch.vehicleYear = year;
  if (normalizeName(values.vehicleMake) !== normalizeName(baseline.vehicleMake)) {
    patch.vehicleMake = values.vehicleMake.trim();
  }
  if (normalizeName(values.vehicleModel) !== normalizeName(baseline.vehicleModel)) {
    patch.vehicleModel = values.vehicleModel.trim();
  }
  if (values.advisorId !== baseline.advisorId) patch.advisorId = values.advisorId;
  if (values.technicianId !== baseline.technicianId) patch.technicianId = trimmedOrNull(values.technicianId);

  const appointment = trimmedOrNull(values.appointmentDate);
  if (appointment !== trimmedOrNull(baseline.appointmentDate)) patch.appointmentDate = appointment;
  const vendor = trimmedOrNull(values.vendorOrderReference);
  if (vendor !== trimmedOrNull(baseline.vendorOrderReference)) patch.vendorOrderReference = vendor;
  const note = trimmedOrNull(values.orderNote);
  if (note !== trimmedOrNull(baseline.orderNote)) patch.orderNote = note;
  const masterEta = trimmedOrNull(values.masterEta);
  if (masterEta !== trimmedOrNull(baseline.masterEta)) patch.masterEta = masterEta;
  if (values.carInShop !== baseline.carInShop) patch.carInShop = values.carInShop;
  return patch;
}

/** The fields of an existing line whose value differs from the snapshot. */
export function linePatch(baseline: EditLineValues, line: EditLineValues): UpdateLineInput["patch"] {
  const patch: { -readonly [K in keyof UpdateLineInput["patch"]]: UpdateLineInput["patch"][K] } = {};
  if (line.partNumber.trim() !== baseline.partNumber.trim()) patch.partNumber = line.partNumber.trim();
  if (line.description.trim() !== baseline.description.trim()) patch.description = line.description.trim();
  const quantity = parseWholeNumber(line.quantityOrdered);
  if (quantity !== null && quantity !== parseWholeNumber(baseline.quantityOrdered)) {
    patch.quantityOrdered = quantity;
  }
  const eta = toEtaInput(line);
  const previous = toEtaInput(baseline);
  if (eta.kind !== previous.kind || (eta.kind === "date" && previous.kind === "date" && eta.date !== previous.date)) {
    patch.eta = eta;
  }
  return patch;
}

/**
 * A line added and saved in this sheet: the server does not answer with the
 * new line's id, so until the sheet reopens on fresh data the line is on
 * record but cannot be edited again from here. The save plan skips it and
 * the lines editor locks its fields (`lineDisabled`), so the two agree.
 */
const SAVED_LINE_PREFIX = "saved:";

export function isSavedUnidentifiedLine(line: EditLineValues): boolean {
  return line.lineId !== null && line.lineId.startsWith(SAVED_LINE_PREFIX);
}

/** The placeholder id a line gets once saved from a sheet (the wholesale sheet marks its lines the same way). */
export function savedLineId(clientId: string): string {
  return `${SAVED_LINE_PREFIX}${clientId}`;
}

export function buildSavePlan(baseline: EditOrderValues, values: EditOrderValues): SaveStep[] {
  const steps: SaveStep[] = [];
  const header = headerPatch(baseline, values);
  if (Object.keys(header).length > 0) steps.push({ kind: "header", patch: header });
  for (const line of values.partLines) {
    if (isSavedUnidentifiedLine(line)) continue;
    if (line.lineId === null) {
      steps.push({
        kind: "add",
        clientId: line.clientId,
        input: {
          partNumber: line.partNumber.trim(),
          description: line.description.trim(),
          quantityOrdered: parseWholeNumber(line.quantityOrdered) ?? Number.NaN,
          eta: toEtaInput(line),
        },
      });
      continue;
    }
    const before = baseline.partLines.find((candidate) => candidate.clientId === line.clientId);
    if (!before) continue;
    const patch = linePatch(before, line);
    if (Object.keys(patch).length > 0) {
      steps.push({ kind: "line", clientId: line.clientId, lineId: line.lineId, patch });
    }
  }
  return steps;
}

/** True when a step can take an Arrived case back to incomplete (spec §7). */
export function stepInvalidatesArrival(step: SaveStep, baseline: EditOrderValues): boolean {
  if (step.kind === "add") return true;
  if (step.kind === "line" && step.patch.quantityOrdered !== undefined) {
    const before = baseline.partLines.find((line) => line.clientId === step.clientId);
    return before !== undefined && step.patch.quantityOrdered > (parseWholeNumber(before.quantityOrdered) ?? 0);
  }
  return false;
}

/**
 * The snapshot and the form after `step` succeeded: the snapshot takes what
 * the server now holds for those fields, and a newly added line gets its
 * placeholder id in both so it is not sent twice.
 */
function applyStep(
  baseline: EditOrderValues,
  values: EditOrderValues,
  step: SaveStep,
): { baseline: EditOrderValues; values: EditOrderValues } {
  if (step.kind === "header") {
    const { partLines: _lines, ...header } = values;
    return { baseline: { ...baseline, ...header }, values };
  }
  if (step.kind === "line") {
    const current = values.partLines.find((candidate) => candidate.clientId === step.clientId);
    return {
      baseline: {
        ...baseline,
        partLines: baseline.partLines.map((line) =>
          line.clientId === step.clientId && current ? { ...line, ...current, lineId: line.lineId } : line,
        ),
      },
      values,
    };
  }
  const added = values.partLines.find((line) => line.clientId === step.clientId);
  if (!added) return { baseline, values };
  const saved: EditLineValues = { ...added, lineId: `${SAVED_LINE_PREFIX}${added.clientId}` };
  return {
    baseline: { ...baseline, partLines: [...baseline.partLines, saved] },
    values: {
      ...values,
      partLines: values.partLines.map((line) => (line.clientId === step.clientId ? saved : line)),
    },
  };
}

// ---------------------------------------------------------------------------
// Validation for the sheet
// ---------------------------------------------------------------------------

export function validateEdit(
  values: EditOrderValues,
  baseline: EditOrderValues,
  status: CaseDetail["case"]["status"],
  correctionReason: string,
  now: Date,
): { errors: OrderFormErrors; correctionError: string | null } {
  const fields = validateHeader(values, now);
  const lines: Record<string, OrderFormLineErrors> = {};
  for (const line of values.partLines) {
    const errors = validateLine(line);
    const quantity = parseWholeNumber(line.quantityOrdered);
    if (!errors.quantityOrdered && quantity !== null && quantity < line.quantityVerified) {
      errors.quantityOrdered = `${line.quantityVerified} already verified. Reverse the verification before ordering fewer.`;
    }
    if (Object.keys(errors).length > 0) lines[line.clientId] = errors;
  }
  const plan = buildSavePlan(baseline, values);
  const needsCorrection =
    status === "arrived" && plan.some((step) => stepInvalidatesArrival(step, baseline));
  const correctionError = needsCorrection
    ? (reasonError(correctionReason) ?? null)
    : correctionReason.trim() !== ""
      ? (reasonError(correctionReason) ?? null)
      : null;
  return {
    errors: {
      fields,
      lines,
      isValid: Object.keys(fields).length === 0 && Object.keys(lines).length === 0 && correctionError === null,
    },
    correctionError,
  };
}

// ---------------------------------------------------------------------------
// The sheet
// ---------------------------------------------------------------------------

export interface EditOrderSheetProps {
  open: boolean;
  onClose: () => void;
  /** Snapshotted when the sheet opens. */
  detail: CaseDetail;
  /** The whole save finished; `result` is the last command's answer. */
  onSaved: (result: CaseMutationResultDto) => void;
  /** An exception was flagged (`exception` set) or cleared (`null`) on `partNumber`. */
  onExceptionChanged: (result: CaseMutationResultDto, partNumber: string, exception: PartException | null) => void;
}

export function EditOrderSheet(props: EditOrderSheetProps) {
  if (!props.open) return null;
  return <EditOrderFlow {...props} />;
}

type DuplicateState = Readonly<{
  candidates: readonly DuplicateCandidate[];
  /** The header step that was refused, then everything after it. */
  steps: readonly SaveStep[];
}>;

type RunOutcome =
  | Readonly<{ kind: "done"; result: CaseMutationResultDto | null }>
  | Readonly<{ kind: "stale" }>
  | Readonly<{ kind: "duplicate"; candidates: readonly DuplicateCandidate[]; steps: readonly SaveStep[] }>
  | Readonly<{ kind: "failed" }>;

function EditOrderFlow({ onClose, detail, onSaved, onExceptionChanged }: EditOrderSheetProps) {
  const queryClient = useQueryClient();
  const idPrefix = useId();
  const correctionId = useId();
  const formRef = useRef<HTMLFormElement>(null);

  // The case as it stood when the sheet opened.
  const [snapshot] = useState(() => ({
    caseId: detail.case.id,
    caseNumber: detail.case.caseNumber,
    status: detail.case.status,
    vehicleLabel: detail.case.vehicleLabel,
    customerName: detail.case.customerName,
    advisor: { id: detail.case.advisorId, displayName: detail.case.advisorName },
    technician:
      detail.case.technicianId !== null && detail.case.technicianName !== null
        ? { id: detail.case.technicianId, displayName: detail.case.technicianName }
        : null,
  }));
  const [version, setVersion] = useState(detail.case.version);
  const [baseline, setBaseline] = useState<EditOrderValues>(() => valuesFromDetail(detail));
  const [values, setValues] = useState<EditOrderValues>(() => valuesFromDetail(detail));
  const [errors, setErrors] = useState<OrderFormErrors>(NO_ERRORS);
  const [correctionReason, setCorrectionReason] = useState("");
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [duplicate, setDuplicate] = useState<DuplicateState | null>(null);
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [exceptionLine, setExceptionLine] = useState<EditLineValues | null>(null);
  const [focusRequest, setFocusRequest] = useState(0);
  // The sheet is modal, so the inspector's live region behind it is hidden
  // from assistive technology; what happens inside the sheet is announced here.
  const [sheetAnnouncement, setSheetAnnouncement] = useState("");

  const assignees = useAssignees();

  // What Save sends: the form less the untouched rows at the end (the row
  // Enter adds after the last line), so a blank row neither dirties the
  // sheet nor is refused.
  const submittable: EditOrderValues = { ...values, partLines: withoutBlankTrailingLines(values.partLines) };
  const plan = buildSavePlan(baseline, submittable);
  const dirty = plan.length > 0;

  const updateHeader = (patch: Partial<OrderFormHeaderValues>) => {
    setValues((current) => ({
      ...current,
      ...patch,
      // "ETA for all parts" fills the blank lines — on the sheet, only rows
      // added here: every line on record already holds a date or No ETA.
      ...(patch.masterEta !== undefined
        ? { partLines: withMasterEta(current.partLines, patch.masterEta) }
        : {}),
    }));
    setErrors((current) => {
      const fields = { ...current.fields };
      let changed = false;
      for (const key of Object.keys(patch) as OrderFormFieldKey[]) {
        if (fields[key]) {
          delete fields[key];
          changed = true;
        }
      }
      return changed ? { ...current, fields } : current;
    });
    if (formError && !stale) setFormError(null);
  };

  const updateLines = (partLines: EditLineValues[]) => {
    const previousLines = values.partLines;
    // A line that goes blank again (No ETA unticked, a date cleared) takes
    // the master ETA back at once: under one, a blank line means that date.
    setValues((current) => ({ ...current, partLines: withMasterEta(partLines, current.masterEta) }));
    setErrors((current) => {
      const lines = clearChangedLineMarks(current.lines, previousLines, partLines);
      return lines === current.lines ? current : { ...current, lines };
    });
  };

  /** Sends one step at `version` and returns the server's answer. */
  const send = (step: SaveStep, at: number, duplicateOverrideReason?: string) => {
    const correction =
      snapshot.status === "arrived" && stepInvalidatesArrival(step, baseline) && correctionReason.trim() !== ""
        ? { correctionReason: correctionReason.trim() }
        : {};
    if (step.kind === "header") {
      const input: UpdateCaseHeaderInput = {
        patch: step.patch,
        meta: { expectedVersion: at, ...correction },
        ...(duplicateOverrideReason !== undefined ? { duplicateOverrideReason } : {}),
      };
      return updateCaseHeader(snapshot.caseId, input);
    }
    if (step.kind === "line") {
      return updateLine(snapshot.caseId, step.lineId, {
        patch: step.patch,
        meta: { expectedVersion: at, ...correction },
      });
    }
    return addLine(snapshot.caseId, { ...step.input, meta: { expectedVersion: at, ...correction } });
  };

  /** Maps a 422's field errors onto the step that was refused. */
  const showFieldErrors = (step: SaveStep, fieldErrors: Readonly<Record<string, readonly string[]>>) => {
    const fields: Partial<Record<OrderFormFieldKey, string>> = {};
    const lines: Record<string, OrderFormLineErrors> = {};
    const unmapped: string[] = [];
    let correction: string | null = null;
    for (const [path, messages] of Object.entries(fieldErrors)) {
      const message = messages.find((text) => text.length > 0) ?? "Check this field.";
      const key = path.startsWith("patch.") ? path.slice("patch.".length) : path;
      if (key === "meta.correctionReason") {
        correction = message;
        continue;
      }
      if (step.kind === "header" && isOrderFormFieldKey(key)) {
        fields[key] = message;
        continue;
      }
      if (step.kind !== "header") {
        const lineKey = lineFieldKey(key.split(".")[0]);
        if (lineKey) {
          lines[step.clientId] = { ...lines[step.clientId], [lineKey]: message };
          continue;
        }
      }
      unmapped.push(message);
    }
    setErrors({ fields, lines, isValid: false });
    setCorrectionError(correction);
    setFormError(unmapped.length > 0 ? unmapped.join(" ") : null);
    setFocusRequest((n) => n + 1);
  };

  const run = useMutation({
    mutationFn: async (input: { steps: readonly SaveStep[]; overrideReason?: string }): Promise<RunOutcome> => {
      let at = version;
      let base = baseline;
      let vals = values;
      let last: CaseMutationResultDto | null = null;
      let savedAny = false;
      const steps = [...input.steps];
      for (let index = 0; index < steps.length; index += 1) {
        const step = steps[index]!;
        try {
          const result = await send(step, at, index === 0 ? input.overrideReason : undefined);
          at = result.version;
          last = result;
          savedAny = true;
          const applied = applyStep(base, vals, step);
          base = applied.baseline;
          vals = applied.values;
          setVersion(at);
          setBaseline(base);
          setValues(vals);
        } catch (error) {
          if (savedAny) void invalidateCaseData(queryClient);
          if (isStaleWriteError(error)) {
            setStale(true);
            setFormError(STALE_WRITE_MESSAGE);
            return { kind: "stale" };
          }
          const candidates = duplicateCandidatesOf(error);
          if (candidates && step.kind === "header") {
            return { kind: "duplicate", candidates, steps: steps.slice(index) };
          }
          const fieldErrors = fieldErrorsOf(error);
          if (fieldErrors) showFieldErrors(step, fieldErrors);
          else setFormError(orderMutationErrorMessage(error));
          return { kind: "failed" };
        }
      }
      if (savedAny) await invalidateCaseData(queryClient);
      return { kind: "done", result: last };
    },
    onSuccess: (outcome) => {
      if (outcome.kind === "done") {
        if (outcome.result) onSaved(outcome.result);
        onClose();
        return;
      }
      if (outcome.kind === "duplicate") {
        setDuplicate({ candidates: outcome.candidates, steps: outcome.steps });
        setOverrideError(null);
      }
    },
  });

  const saving = run.isPending;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving || stale || !dirty) return;
    const validation = validateEdit(submittable, baseline, snapshot.status, correctionReason, new Date());
    setErrors(validation.errors);
    setCorrectionError(validation.correctionError);
    setFormError(null);
    if (!validation.errors.isValid) {
      setFocusRequest((n) => n + 1);
      return;
    }
    run.mutate({ steps: plan });
  };

  const proceedWithOverride = (reason: string) => {
    if (!duplicate || saving) return;
    setOverrideError(null);
    run.mutate(
      { steps: duplicate.steps, overrideReason: reason },
      {
        onSuccess: (outcome) => {
          if (outcome.kind === "duplicate") {
            setOverrideError("The server still sees a duplicate. Check the reason and try again.");
            return;
          }
          // Anything else was reported on the sheet itself (or finished it).
          if (outcome.kind !== "done") setDuplicate(null);
        },
      },
    );
  };

  const requestClose = () => {
    if (saving) return;
    if (dirty) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const reloadLatest = () => {
    void queryClient.invalidateQueries({ queryKey: caseQueryKeys.detail(snapshot.caseId) });
    onClose();
  };

  // Focus the first invalid control once the marks have rendered.
  useEffect(() => {
    if (focusRequest > 0) focusFirstInvalid(formRef.current);
  }, [focusRequest]);

  const onExceptionDone = (line: EditLineValues, result: CaseMutationResultDto, exception: PartException | null, reason: string) => {
    setVersion(result.version);
    const patchLine = (candidate: EditLineValues) =>
      candidate.clientId === line.clientId
        ? { ...candidate, exception, exceptionReason: exception ? reason : null }
        : candidate;
    setValues((current) => ({ ...current, partLines: current.partLines.map(patchLine) }));
    setBaseline((current) => ({ ...current, partLines: current.partLines.map(patchLine) }));
    setExceptionLine(null);
    const saved = baseline.partLines.find((candidate) => candidate.clientId === line.clientId);
    const partNumber = saved?.partNumber ?? line.partNumber;
    setSheetAnnouncement(
      exception
        ? `${exceptionLabel(exception)} flagged on ${partNumber}.`
        : `Exception cleared on ${partNumber}.`,
    );
    onExceptionChanged(result, partNumber, exception);
  };

  const renderLineExtras = (line: EditLineValues) => {
    if (line.lineId === null) return null;
    if (isSavedUnidentifiedLine(line)) {
      return (
        <div className="part-line__status">
          <span className="part-line__verified">Saved — reopen the order to edit or flag this line.</span>
        </div>
      );
    }
    const saved = baseline.partLines.find((candidate) => candidate.clientId === line.clientId) ?? line;
    return (
      <div className="part-line__status">
        {line.quantityVerified > 0 ? (
          <span className="part-line__verified">{line.quantityVerified} verified</span>
        ) : null}
        {line.exception ? (
          <>
            <span className="inspector__exception" data-exception={line.exception}>
              {exceptionLabel(line.exception)}
              {line.exceptionReason ? ` — ${line.exceptionReason}` : ""}
            </span>
            <Button
              variant="quiet"
              icon={<FlagOff />}
              disabled={saving || stale}
              onClick={() => setExceptionLine(line)}
            >
              {"Clear exception "}
              <span className="visually-hidden">on {saved.partNumber}</span>
            </Button>
          </>
        ) : (
          <Button
            variant="quiet"
            icon={<Flag />}
            disabled={saving || stale}
            onClick={() => setExceptionLine(line)}
          >
            {"Flag exception "}
            <span className="visually-hidden">on {saved.partNumber}</span>
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
      <Dialog
        open
        onClose={requestClose}
        title="Edit order"
        description={`${snapshot.caseNumber} · ${snapshot.vehicleLabel} — ${snapshot.customerName}`}
        size="large"
        pending={saving}
      >
        <form ref={formRef} className="order-form order-form--sheet" noValidate onSubmit={submit}>
          <p
            role="status"
            aria-live="polite"
            className={`inspector__announcement${sheetAnnouncement ? "" : " inspector__announcement--empty"}`}
          >
            {sheetAnnouncement}
          </p>
          {formError ? (
            <div role="alert" className="form-alert order-form__alert">
              <p className="order-form__alert-text">{formError}</p>
              {stale ? (
                <Button variant="secondary" onClick={reloadLatest}>
                  Reload latest
                </Button>
              ) : null}
            </div>
          ) : null}

          <OrderFormSection title="Vehicle and visit" level={3}>
            <VehicleAndVisitFields
              values={values}
              errors={errors.fields}
              onChange={updateHeader}
              disabled={saving || stale}
              idPrefix={idPrefix}
            />
          </OrderFormSection>

          <OrderFormSection title="People" level={3}>
            <PeopleFields
              values={values}
              errors={errors.fields}
              onChange={updateHeader}
              disabled={saving || stale}
              idPrefix={idPrefix}
              assignees={assignees.data}
              assigneesLoading={assignees.isPending}
              assigneesError={assignees.error}
              onRetryAssignees={() => {
                void assignees.refetch();
              }}
              currentAdvisor={snapshot.advisor}
              currentTechnician={snapshot.technician}
            />
          </OrderFormSection>

          <OrderFormSection
            title="Parts ordered"
            level={3}
            description="Existing lines stay on record — flag one as a cancelled line rather than removing it."
          >
            <MasterEtaField
              value={values.masterEta}
              error={errors.fields.masterEta}
              onChange={(masterEta) => updateHeader({ masterEta })}
              disabled={saving || stale}
              idPrefix={idPrefix}
            />
            <PartLinesGrid<EditLineValues>
              lines={values.partLines}
              errors={errors.lines}
              onChange={updateLines}
              createLine={() => newEditLine(values.masterEta)}
              canRemove={(line) => line.lineId === null}
              minQuantity={(line) => Math.max(LIMITS.quantity.min, line.quantityVerified)}
              // A line saved from this sheet is on record but cannot be edited
              // again until the sheet reopens: its fields refuse input, so
              // nothing typed there is dropped by the next Save.
              lineDisabled={isSavedUnidentifiedLine}
              renderLineExtras={renderLineExtras}
              disabled={saving || stale}
              idPrefix={idPrefix}
            />
          </OrderFormSection>

          <OrderFormSection title="Optional details" level={3}>
            <OptionalDetailsFields
              values={values}
              errors={errors.fields}
              onChange={updateHeader}
              disabled={saving || stale}
              idPrefix={idPrefix}
            />
          </OrderFormSection>

          {snapshot.status === "arrived" ? (
            <OrderFormSection title="Correction" level={3}>
              <FormField
                id={correctionId}
                label="Correction reason"
                optional
                hint="Needed when this change takes the order out of Arrived — raising a quantity or adding a part. It is kept in the activity history and recipients get an Arrival corrected email."
                error={correctionError}
                className="order-form__field--wide"
              >
                {(control) => (
                  <textarea
                    {...control}
                    className="text-input textarea-input"
                    data-size="desktop"
                    rows={2}
                    value={correctionReason}
                    disabled={saving || stale}
                    onChange={(event) => {
                      setCorrectionReason(event.target.value);
                      if (correctionError) setCorrectionError(null);
                    }}
                  />
                )}
              </FormField>
            </OrderFormSection>
          ) : null}

          <DialogActions>
            <Button variant="secondary" onClick={requestClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" busy={saving} disabled={!dirty || stale}>
              Save changes
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
        title="Discard unsaved changes?"
        description="Your edits to this order haven't been saved. If you close now, they're lost."
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        tone="destructive"
      />

      <DuplicateWarningDialog
        open={duplicate !== null}
        candidates={duplicate?.candidates ?? []}
        onClose={() => setDuplicate(null)}
        onProceed={proceedWithOverride}
        pending={saving}
        error={overrideError}
        proceedLabel="Save anyway"
      />

      <LineExceptionDialog
        open={exceptionLine !== null}
        caseId={snapshot.caseId}
        version={version}
        line={exceptionLine}
        partNumber={
          exceptionLine
            ? (baseline.partLines.find((candidate) => candidate.clientId === exceptionLine.clientId)?.partNumber ??
              exceptionLine.partNumber)
            : ""
        }
        onClose={() => setExceptionLine(null)}
        onDone={onExceptionDone}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Flag / clear an exception
// ---------------------------------------------------------------------------

/**
 * The same dialog flags a line on a wholesale order (wholesale spec §4.5):
 * handed that order's `endpoint` it posts to the wholesale route. `R` is
 * the side's mutation result.
 */
export interface LineExceptionDialogProps<R = CaseMutationResultDto> {
  open: boolean;
  caseId: string;
  /** The case version to send; the answer's version replaces it. */
  version: number;
  line: EditLineValues | null;
  /** The saved part number, for the title. */
  partNumber: string;
  /** Where the exception is posted; defaults to the case route for `caseId`. */
  endpoint?: LineExceptionEndpoint<R>;
  onClose: () => void;
  onDone: (line: EditLineValues, result: R, exception: PartException | null, reason: string) => void;
}

export function LineExceptionDialog<R = CaseMutationResultDto>(props: LineExceptionDialogProps<R>) {
  if (!props.open || !props.line || props.line.lineId === null) return null;
  return <LineExceptionFlow {...props} line={props.line} lineId={props.line.lineId} />;
}

function LineExceptionFlow<R>({
  caseId,
  version,
  line,
  lineId,
  partNumber,
  endpoint,
  onClose,
  onDone,
}: LineExceptionDialogProps<R> & { line: EditLineValues; lineId: string }) {
  const clearing = line.exception !== null;
  const [exception, setException] = useState<PartException | "">("");
  const [reason, setReason] = useState("");
  const [exceptionError, setExceptionError] = useState<string | null>(null);
  const [reasonErrorText, setReasonErrorText] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const exceptionId = useId();
  const reasonId = useId();
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const exceptionRef = useRef<HTMLSelectElement>(null);
  // Without an endpoint the dialog works the case route, whose result is the default `R`.
  const [api] = useState(
    () =>
      endpoint ??
      (((target, input) => setLineException(caseId, target, input)) as LineExceptionEndpoint as unknown as LineExceptionEndpoint<R>),
  );
  const mutation = useSetLineException(api);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (mutation.isPending) return;
    const reasonProblem = reasonError(reason) ?? null;
    const exceptionProblem = !clearing && exception === "" ? "Choose the exception." : null;
    setReasonErrorText(reasonProblem);
    setExceptionError(exceptionProblem);
    setFormError(null);
    if (exceptionProblem) {
      exceptionRef.current?.focus();
      return;
    }
    if (reasonProblem) {
      reasonRef.current?.focus();
      return;
    }
    const next: PartException | null = clearing ? null : (exception as PartException);
    mutation.mutate(
      {
        lineId,
        input: { exception: next, reason: reason.trim(), meta: { expectedVersion: version } },
      },
      {
        onSuccess: (result) => onDone(line, result, next, reason.trim()),
        onError: (error) => {
          const fieldErrors = fieldErrorsOf(error);
          const reasonMessage = fieldErrors?.["reason"]?.find((text) => text.length > 0);
          if (reasonMessage) {
            setReasonErrorText(reasonMessage);
            return;
          }
          setFormError(orderMutationErrorMessage(error));
        },
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={clearing ? `Clear the exception on ${partNumber}` : `Flag an exception on ${partNumber}`}
      description={
        clearing
          ? `${exceptionLabel(line.exception ?? "backordered")} will be cleared and the line goes back to normal. The exception and your reason stay in the activity history.`
          : "A flagged line keeps the order out of Arrived until Parts clears it with a reason or cancels the order. Your reason is kept in the activity history."
      }
      pending={mutation.isPending}
      size="small"
      initialFocusRef={clearing ? reasonRef : exceptionRef}
    >
      <form noValidate onSubmit={submit} className="exception-form">
        {!clearing ? (
          <FormField id={exceptionId} label="Exception" error={exceptionError}>
            {(control) => (
              <select
                {...control}
                ref={exceptionRef}
                className="text-input select-input"
                data-size="desktop"
                value={exception}
                disabled={mutation.isPending}
                onChange={(event) => {
                  setException(event.target.value as PartException | "");
                  if (exceptionError) setExceptionError(null);
                }}
              >
                <option value="">Choose…</option>
                {PART_EXCEPTIONS.map((candidate) => (
                  <option key={candidate} value={candidate}>
                    {exceptionLabel(candidate)}
                  </option>
                ))}
              </select>
            )}
          </FormField>
        ) : null}
        <FormField id={reasonId} label="Reason" error={reasonErrorText}>
          {(control) => (
            <textarea
              {...control}
              ref={reasonRef}
              className="text-input textarea-input"
              data-size="desktop"
              rows={3}
              value={reason}
              disabled={mutation.isPending}
              onChange={(event) => {
                setReason(event.target.value);
                if (reasonErrorText) setReasonErrorText(null);
              }}
            />
          )}
        </FormField>
        {formError ? (
          <p role="alert" className="form-alert">
            {formError}
          </p>
        ) : null}
        <DialogActions>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" busy={mutation.isPending}>
            {clearing ? "Clear exception" : "Flag exception"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

